import { useCallback, useEffect, useMemo, useState } from 'react'
import usePageTitle from '../../../util/usePageTitle'
import { useParams, useNavigate } from 'react-router-dom'
import PageContainer from '../../../components/layout/PageContainer'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Grid from '@mui/material/Grid'
import AuditConfigEdit from '../AuditConfigEdit'
import { RAuditConfig } from '@counsel-project/counsel-audit-backend-api'
import { auditBackendRequest } from '../../../util/api/audit-backend-api'
import checkToken from '../../../util/auth/checkToken'
import Tooltip from '@mui/material/Tooltip'
import { getAuditConfigSavingProblems, stableStringify } from '../helpers'
import handleError from '../../../util/handleError'
import toast from 'react-hot-toast'

const EditAuditConfigPage = () => {
  usePageTitle('Edit Audit Configuration')
  const { auditConfigId } = useParams()

  const [config, setConfig] = useState<RAuditConfig | null>(null)
  // Snapshot of the last loaded/saved state, used to detect unsaved edits
  const [savedConfig, setSavedConfig] = useState<RAuditConfig | null>(null)
  const [loading, setLoading] = useState(false)

  const saveProblems = useMemo(() => getAuditConfigSavingProblems(config), [config])

  const hasUnsavedChanges = useMemo(
    () => !!config && !!savedConfig && stableStringify(config) !== stableStringify(savedConfig),
    [config, savedConfig]
  )

  const navigate = useNavigate()

  const populateConfig = useCallback(async () => {
    try {
      setLoading(true)

      if (!auditConfigId) {
        toast.error('Please provide a configuration ID to view this page')
        return
      }

      const token = await checkToken()
      if (!token) return

      const { result: auditConfig } = await auditBackendRequest.auditConfigs.get({
        token: '',
        auditConfigId,
      })

      setConfig(auditConfig)
      setSavedConfig(auditConfig)
    } catch (error) {
      handleError(error)
    } finally {
      setLoading(false)
    }
  }, [auditConfigId])

  useEffect(() => {
    if (auditConfigId) {
      populateConfig()
    }
  }, [auditConfigId, populateConfig])

  const handleSave = useCallback(async () => {
    try {
      setLoading(true)

      if (!config) return

      const token = await checkToken()
      if (!token) return

      const { result: updatedConfig } = await auditBackendRequest.auditConfigs.update({
        token: '',
        auditConfigId: config._id,
        state: config.state,
        programs: config.programs,
        locations: config.locations,
        enabled: config.enabled,
        displayComplianceCheckButton: config.displayComplianceCheckButton,
        autoComplianceCheck: config.autoComplianceCheck,
        title: config.title,
        minimumSeverityForRealTimeAlerts: config.minimumSeverityForRealTimeAlerts,
        measures: config.measures,
      })

      toast.success('Configuration updated successfully')
      // The server's canonical copy becomes the new unsaved-changes baseline.
      // Only adopt it into the editor when nothing changed mid-request, so
      // edits made while the save was in flight are kept (and correctly
      // flagged as unsaved against the new baseline).
      setSavedConfig(updatedConfig)
      setConfig((current) => (current === config ? updatedConfig : current))
      navigate(`/configure/${config._id}`)
    } catch (error) {
      handleError(error)
    } finally {
      setLoading(false)
    }
  }, [config, navigate])

  const handleDelete = useCallback(async () => {
    try {
      setLoading(true)

      if (!auditConfigId) {
        toast.error('No configuration ID found')
        return
      }

      const token = await checkToken()
      if (!token) return

      await auditBackendRequest.auditConfigs.delete({
        token: '',
        auditConfigId,
      })

      toast.success('Configuration deleted successfully')
      navigate('/configure')
    } catch (error) {
      handleError(error)
    } finally {
      setLoading(false)
    }
  }, [auditConfigId, navigate])

  return (
    <PageContainer>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/configure')}
          data-qa-id="config-go-back-button"
        >
          Go Back
        </Button>
      </Box>
      {config && (
        <>
          <AuditConfigEdit
            auditConfig={config}
            hasUnsavedChanges={hasUnsavedChanges}
            onChangeAuditConfig={setConfig}
            onDelete={handleDelete}
          />
          <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
            <Grid item>
              <Tooltip title={saveProblems.join(', ')}>
                <span>
                  <Button
                    onClick={handleSave}
                    disabled={loading || saveProblems.length > 0}
                    data-qa-id="config-save-button"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </>
      )}
    </PageContainer>
  )
}

export default EditAuditConfigPage
